/**
 * "Jane Doe, MD" — a provider's name with their post-nominals appended, as a
 * single readable string (VIT-18200).
 *
 * One helper rather than a separator per call site, because the separator has to
 * live in the *text*, not in a margin. `<h2>{name}<span class="ml-2">{creds}</span></h2>`
 * looks right and reads "Patricia OlsonPhD, MD" — which is what Google scraped
 * into the results-page SERP snippet, and what a screen reader announces. Every
 * place that shows a name beside credentials composes it here so a third caller
 * can't reintroduce the gap.
 *
 * Empty when there is no name: a bare ", MD" names nobody. Callers that need to
 * know the difference test for `''`.
 *
 * A leading comma on `credentials` is dropped rather than doubled — the field is
 * arbitrary upstream text, and "Olson,, MD" is a worse failure than the one this
 * fixes.
 */
export const formatProviderName = (name: string | null | undefined, credentials: string | null | undefined): string => {
  const trimmedName = name?.trim() ?? ''
  if (trimmedName === '') return ''

  const suffix = formatCredentials(credentials)
  return suffix === '' ? trimmedName : `${trimmedName}${suffix}`
}

/**
 * The same name, but as the opening of a longer sentence: "Jane C. Doe, MD," when
 * something follows it, "Jane C. Doe, MD" when nothing does (VIT-18212).
 *
 * Post-nominals mid-sentence take a comma on BOTH sides — "Jane C. Doe, MD, in
 * Austin, TX", not "Jane C. Doe, MD in Austin, TX" — and the closing one is the
 * whole reason this is a separate function. It cannot live in `formatProviderName`,
 * because that string is also a heading and an alt text, where a trailing comma
 * would dangle off the end.
 *
 * The two callers are the provider page's `<title>` and its `<meta name="description">`
 * (see routes/provider.tsx and provider-description.ts). One helper rather than the
 * rule written twice, because they have to agree: the description's opening clause
 * is the string the title is built from, and a comma that appeared in one and not
 * the other would read as a typo in whichever surface the reader saw second.
 *
 * `continues` is the caller's answer to "is there more sentence after this?" — it
 * is false for a provider with no specialty and no address, where the name is the
 * whole lead and a closing comma would trail into nothing.
 *
 * Empty when there is no name, same as `formatProviderName` and for the same reason.
 */
export const formatProviderNameLead = (
  name: string | null | undefined,
  credentials: string | null | undefined,
  continues: boolean,
): string => {
  const named = formatProviderName(name, credentials)
  if (named === '' || !continues) return named

  // Only credentials open a comma pair that needs closing. A bare name followed by
  // " in Austin, TX" reads correctly with no comma at all, and adding one there
  // would punctuate a clause that isn't parenthetical.
  return formatCredentials(credentials) === '' ? named : `${named},`
}

/**
 * The post-nominals as they follow a name — ", MD" — or `''` when there are none.
 *
 * Exported for the one caller that can't use the joined string: the results card
 * styles the credentials differently from the name, so it needs them in their own
 * <span>. The separator travels *inside* that span, which is the whole point —
 * see the note above.
 */
export const formatCredentials = (credentials: string | null | undefined): string => {
  const trimmed = (credentials ?? '').replace(/^[\s,]+/u, '').trim()
  return trimmed === '' ? '' : `, ${trimmed}`
}
